import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { authApi } from './api';
import { useAuthStore } from '@/store/auth.store';
import { getErrorMessage } from '@/shared/api/client';

export const useLogin = () => {
  const setSession = useAuthStore((s) => s.setSession);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) => authApi.login(email, password),
    onSuccess: (data) => {
      setSession(data);
      toast.success(`Welcome back, ${data.user.displayName}`);
      navigate('/');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });
};

export const useLogout = () => {
  const clearSession = useAuthStore((s) => s.clearSession);
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: () => {
      clearSession();
      qc.clear();
      navigate('/login', { replace: true });
    },
  });
};

export const useMe = () => {
  const setSession = useAuthStore((s) => s.setSession);
  const setHydrating = useAuthStore((s) => s.setHydrating);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const me = await authApi.me();
      const token = useAuthStore.getState().accessToken;
      const exp = useAuthStore.getState().accessExpiresAtUtc;
      if (token && exp) {
        setSession({ accessToken: token, accessExpiresAtUtc: exp, user: me });
      }
      setHydrating(false);
      return me;
    },
    retry: false,
    enabled: !!useAuthStore.getState().accessToken,
  });
};
